<template>

    <div class="attachment-list pb-3">

        <b-row
            class="my-3 pb-1 d-flex flex-column flex-sm-row flex-wrap justify-content-center justify-content-sm-between align-items-center">
            <b-col cols="12" sm="5">
                <h3 class="mb-0">Uploaded Documents
                    <helper :tag="helpTag"/>
                </h3>
            </b-col>

        </b-row>

        <b-table striped
                 :items="attachmentsForGroup"
                 ref="table"
                 :fields="fields">

            <template v-slot:cell()="row">
                <!-- description -->
                <div v-if="row.field.key === 'description'">
                    <template v-if="editItem && (editItem === row.item.id)">
                        <b-form-textarea type="text"
                                         v-model="editValue"/>
                        <b-button @click="save(row.item)" size="sm" variant="primary" class="float-right mt-1 ml-1">
                            <b-icon-check2/>
                        </b-button>
                        <b-button @click="cancel()" size="sm" variant="danger" class="float-right mt-1 ml-1">
                            <b-icon-x/>
                        </b-button>
                    </template>
                    <template v-else>
                        {{ row.item[row.field.key] }}
                        <b-icon-pencil-square v-if="!readOnly && !editItem"
                                              @click="edit(row.item, row.item[row.field.key])"
                                              size="sm" class="btn-micro float-right ml-1"/>
                    </template>
                </div>
                <!-- type -->
                <div v-else-if="row.field.key === 'type'">
                    {{ getTypeLabel(row.item[row.field.key]) }}
                </div>
                <!-- size -->
                <div v-else-if="row.field.key === 'size'">
                    {{ getSizeLabel(row.item[row.field.key]) }}
                </div>
                <!-- status -->
                <div v-else-if="row.field.key === 'status'">
                    {{ getStatusLabel(row.item[row.field.key]) }}
                </div>
                <!-- actions -->
                <div v-else-if="row.field.key === 'actions'" class="action-buttons d-flex">
                    <b-button @click="download(row.item, true)"
                              class="btn-icon" size="sm" variant="secondary"
                              :disabled="row.item.status !== 'VALIDATED'">
                        <b-icon-eye/>
                    </b-button>
                    <b-button @click="download(row.item, false)"
                              size="sm" variant="secondary" class="btn-icon ml-2"
                              :disabled="row.item.status !== 'VALIDATED'">
                        <b-icon-download/>
                    </b-button>
                    <b-button v-if="!readOnly"
                              size="sm" variant="danger" class="btn-icon ml-2"
                              @click="remove(row.item)">
                        <b-icon-trash/>
                    </b-button>
                    <div class="ml-2"
                         v-if="!row.item.file_exists">
                        <b-badge variant="danger">
                            File not found
                        </b-badge>
                    </div>
                </div>
                <!-- otherwise -->
                <div v-else>
                    {{ row.item[row.field.key] }}
                </div>
            </template>
        </b-table>
        <div
            v-if="!readOnly"
            class="text-right mt-3">
            <b-button v-if="hasAttachments"
                      variant="primary"
                      class="mr-3"
                      v-on:click="refresh()">
                Refresh
                <b-icon-arrow-repeat/>
            </b-button>
            <file-upload
                class="btn btn-primary btn-icon"
                :input-id="group"
                :post-action="postAction"
                :extensions="extensions"
                :accept="accept"
                :headers="{Authorization: $store.state.currentUser.auth_key}"
                :multiple="false"
                :size="1024 * 1024 * 20"
                v-model="files"
                :customAction="customAction"
                @input-file="inputFile"
                ref="upload">
                Upload
                <b-icon-upload/>
            </file-upload>
        </div>

    </div>

</template>

<script>

/**
 * Component to list attachments for a model, and allow uploading or removing attachments.
 */

import FileUpload from 'vue-upload-component'
import * as _ from 'lodash'
import Attachment from '@/models/Attachment'
import Authorization from '@/providers/Authorization'
import Utils from '@/providers/Utils'

export default {
    name: 'AttachmentList',
    components: {
        FileUpload
    },
    props: {
        // NB: we could probably compute one of modelClass/modelTable from the other,
        // but it's complicated, so let's just be explicit...
        modelClass: String, // unqualified PHP class name of model (used in attachment table)
        modelTable: String, // PHP controller path for model (used in api requests)
        modelId: Number,
        group: {
            type: String,
            default: 'upload'
        },
        extensions: {
            type: String, // allowable file extensions - e.g. "pdf, jpg"
            default: Attachment.getExtensions()
        },
        accept: {
            type: String,     // accepted content types - e.g. "application/pdf, image/jpeg"
            default: Attachment.getMimeTypes()
        },
        readOnly: {
            type: Boolean,
            default: true
        },
        helpTag: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            modelInstance: Object,
            files: [],
            types: [],
            fields: [
                { key: 'name', sortable: true, label: 'Name' },
                { key: 'description', sortable: true, label: 'Description' },
                { key: 'type', sortable: true, label: 'Type' },
                { key: 'size', sortable: true, label: 'Size' },
                { key: 'status', sortable: true, label: 'Status' },
                { key: 'actions', sortable: false },
            ],
            editItem: null,
            editValue: null,
            editingItem: {},
            editMode: null, // 'UPDATE' / 'CREATE'
        }
    },
    computed: {
        attachmentsForGroup () {
            return _.filter(this.modelInstance.attachments, (att) => att.group === this.group)
        },
        hasAttachments () {
            return ((this.attachmentsForGroup || []).length > 0)
        },
        postAction () {
            return this.$api.getAttachmentUploadUrl(this.modelClass, this.modelId, this.group)
        }
    },
    watch: {
        files: {
            deep: true,
            handler: function (newVal, oldVal) {
                for (let file of this.files) {
                    if (file.error) {
                        this.$toasted.error(`Unauthorized or unsupported file extension detected`)
                    }
                }
            },
        }
    },
    methods: {
        download (att, preview) {
            this.$api.downloadAttachment(att, preview)
                .catch((err) => {
                    this.$toasted.error(`Could not download file`)
                })
        },
        edit (att) {
            this.editItem = att.id
            this.editValue = att.description
        },
        cancel (att) {
            this.editItem = null
        },
        async save (att) {
            let attachment = await this.$api.getItem(this, 'attachment', att.id)
            if (attachment) {
                if (await Authorization.can({ action: 'edit', model: 'attachment', arg: att.id })) {
                    attachment.description = this.editValue
                    return this.$api.updateItem(this, 'attachment', attachment)
                        .then(async (response) => {
                            this.editItem = null
                            this.editValue = null
                            this.refresh()
                        })
                        .catch(() => {
                            this.$toasted.error(`Unable to save description`)
                        })
                } else {
                    this.$toasted.error('Authorization error for updating description')
                }
            }
        },
        remove (att) {
            if (Authorization.can({ action: 'edit', model: 'attachment', arg: att.id })) {
                if (confirm('Delete attached file?')) {
                    this.$api.deleteItem(this, 'attachment', att)
                        .then(this.refresh())
                        .catch(() => {
                            this.$toasted.error(`Could not delete file`)
                        })
                }
            } else {
                this.$toasted.error('Authorization error for deleting document')
            }
        },
        inputFile (newFile, oldFile) {
            // when a file is selected, auto-start upload
            if (newFile && !oldFile) {
                this.$refs.upload.active = true
            }
        },
        refresh () {
            // reload the parent model and its attachment list
            this.$api.getItem(this, this.modelTable, this.modelId)
                .then((item) => {
                    this.modelInstance = item
                })
        },
        customAction (file, fileUpload) {
            // double-check access
            if (Authorization.can({ action: 'edit', model: this.modelTable, arg: this.modelId })) {
                // intercept postAction to display potential server errors
                return fileUpload.uploadHtml5(file)
                    .then(result => result)
                    .catch(error => {
                        this.$toasted.error(`${error} error while trying to upload "${file.name}"`)
                        throw error
                    })
            } else {
                this.$toasted.error('Upload authorization error')
            }
        },
        getTypeLabel (type) {
            return Attachment.getTypeLabel(type)
        },
        getSizeLabel (size) {
            return Utils.formatSize(size)
        },
        getStatusLabel (status) {
            return Attachment.getStatusLabel(status)
        },
    },
    mounted () {
        this.refresh()

        // refresh after a file is uploaded
        this.$watch(
            () => {
                return (this.readOnly ? null : this.$refs.upload.uploaded)
            },
            (val) => {
                if (val) {
                    this.refresh()
                }
            }
        )
    },

}
</script>

<style lang="scss">
.attachment-list {
    .btn-micro {
        cursor: pointer;
        color: var(--link-color);

        &:hover, &:focus {
            color: black;
        }
    }
}

.file-uploads input {
    cursor: pointer;
}
</style>
